// @ts-nocheck
import React, { useState, useEffect, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { devicesApi } from "../../../Api/devices.api";
import { useDevice } from "../../../Api/newSockets.api";
import dataApi from "../../../Api/data.api";
import "../../../styles/mes.scss";

const THEME_COLOR = '#00bcd4';

const REST_METRICS_CONFIG: Record<string, { label: string; unit: string; color: string; decimals?: number }> = {
  temp_c:   { label: 'Temperature',      unit: '°C',  color: '#ff9800', decimals: 2 },
  level_mm: { label: 'Level',            unit: 'mm',  color: '#00bcd4', decimals: 1 },
  mA_temp:  { label: 'Temp Current',     unit: 'mA',  color: '#ffb74d', decimals: 3 },
  mA_lvl:   { label: 'Level Current',    unit: 'mA',  color: '#4dd0e1', decimals: 3 },
  fault:    { label: 'Fault',            unit: '',    color: '#f44336', decimals: 0 },
};

const CHART_GROUPS = {
  temperature: {
    label: 'Temperature',
    unit: '°C',
    color: '#ff9800',
    metrics: ['temp_c'],
  },
  level: {
    label: 'Level',
    unit: 'mm',
    color: '#00bcd4',
    metrics: ['level_mm'],
  },
  current: {
    label: 'Sensor Current',
    unit: 'mA',
    color: '#ffb74d',
    metrics: ['mA_temp', 'mA_lvl'],
  },
};

interface Props {
  device: any;
  deviceId: number;
  metrics: any;
  isOnline: boolean;
}

const RestDeviceMetrics: React.FC<Props> = ({ device, deviceId, metrics, isOnline }) => {
  const [timeFilter, setTimeFilter] = useState<'live' | '1h' | '6h' | '24h'>('1h');
  const [metricsData, setMetricsData] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<'url' | 'token' | null>(null);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [selectedDatasetId, setSelectedDatasetId] = useState<number | "">(
    device?.settings?.defaultDataset ?? device?.settings?.defaultDataset ?? ""
  );
  const [newDatasetName, setNewDatasetName] = useState("");
  const [creatingDataset, setCreatingDataset] = useState(false);

  const { updateDeviceSettings } = useDevice();

  // Datasets are fetched over REST (request/response) rather than via the socket
  // 'datasets' event, which is race-prone on a hard reload: the emit and the event
  // listener attach in separate effects, so a fast response can arrive before the
  // listener exists and is silently dropped, leaving this dropdown empty. A REST GET
  // on mount has no such race and is retryable.
  const [datasets, setDatasets] = useState<any[]>([]);

  const fetchDatasets = async () => {
    try {
      const res = await dataApi.getDatasets(0, 200, { type: 'Metrics' });
      setDatasets(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch datasets:', err);
    }
  };

  // Fetch datasets once on mount via REST (no socket-event timing race)
  useEffect(() => {
    fetchDatasets();
  }, []);

  const datasetOptions = useMemo(
    () => datasets?.map((d: any) => ({ value: d.id, label: d.name })) || [],
    [datasets]
  );

  useEffect(() => {
    const id = device?.settings?.defaultDataset ?? device?.settings?.defaultDataset;
    if (id != null) setSelectedDatasetId(Number(id));
  }, [device]);

  // Accumulate real-time metrics
  useEffect(() => {
    const arr = metrics ? (Array.isArray(metrics) ? metrics : [metrics]) : [];
    const forThis = arr.filter((m: any) => m && Number(m.deviceId) === deviceId);
    if (forThis.length === 0) return;

    setMetricsData((prev) => {
      const updated = { ...prev };
      forThis.forEach((met: any) => {
        Object.entries(met).forEach(([key, value]) => {
          if (key !== 'deviceId' && key !== 'organizationId' && typeof value === 'number') {
            updated[key] = [...(updated[key] || []), value];
          }
        });
      });
      return updated;
    });
  }, [metrics, deviceId]);

  // Load historical metrics
  useEffect(() => {
    if (!device) return;
    const load = async () => {
      setLoading(true);
      try {
        const now = new Date();
        const offsets: Record<string, number> = { '1h': 1, '6h': 6, '24h': 24 };
        const hours = offsets[timeFilter] ?? 1;
        const from = new Date(now.getTime() - hours * 60 * 60 * 1000);
        const historical = await devicesApi.getDeviceMetrics(deviceId, { from });
        if (historical?.length > 0) {
          const data: Record<string, any[]> = {};
          historical.forEach((rec: any) => {
            Object.entries(rec.metrics || {}).forEach(([key, value]) => {
              if (typeof value === 'number') {
                data[key] = [...(data[key] || []), value];
              }
            });
          });
          setMetricsData(data);
        }
      } catch (err) {
        console.error('Failed to load REST device metrics:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [device, deviceId, timeFilter]);

  const chartGroups = useMemo(() => {
    const groups: any[] = [];
    Object.entries(CHART_GROUPS).forEach(([groupKey, groupConfig]) => {
      const datasets: any[] = [];
      groupConfig.metrics.forEach((metricKey) => {
        const data = metricsData[metricKey];
        if (!data?.length) return;
        const config = REST_METRICS_CONFIG[metricKey];
        if (!config) return;
        datasets.push({
          label: config.label,
          data: data.slice(-60),
          borderColor: config.color,
          backgroundColor: `${config.color}20`,
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 3,
        });
      });
      if (datasets.length > 0) {
        groups.push({
          key: groupKey,
          label: groupConfig.label,
          unit: groupConfig.unit,
          color: groupConfig.color,
          data: { labels: Array(60).fill(''), datasets },
        });
      }
    });
    return groups;
  }, [metricsData]);

  const chartOptions = (unit: string) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: { boxWidth: 10, boxHeight: 10, padding: 8, font: { size: 11 }, color: '#c5c5c5', usePointStyle: true },
      },
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(33, 40, 44, 0.95)',
        titleColor: '#c5c5c5',
        bodyColor: '#c5c5c5',
        callbacks: {
          label: (ctx: any) => `${ctx.dataset.label}: ${ctx.raw?.toFixed(2) ?? '0'}${unit}`,
        },
      },
    },
    scales: {
      x: { display: false },
      y: {
        display: true,
        grid: { color: 'rgba(108, 117, 125, 0.1)' },
        ticks: { font: { size: 10 }, color: '#6c757d', maxTicksLimit: 5 },
        title: { display: true, text: unit, color: '#6c757d', font: { size: 10 } },
      },
    },
    interaction: { mode: 'nearest' as const, axis: 'x' as const, intersect: false },
  });

  const copyToClipboard = (text: string, field: 'url' | 'token') => {
    if (!text || text === '—') return;
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleDatasetChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value ? Number(e.target.value) : "";
    setSelectedDatasetId(value);
    if (value) await updateDeviceSettings(deviceId, { ...device.settings, defaultDataset: value });
  };

  const handleCreateDataset = async () => {
    if (!newDatasetName.trim()) return;
    setCreatingDataset(true);
    try {
      const nd = await dataApi.createDataset(newDatasetName.trim());
      if (nd?.id) {
        setSelectedDatasetId(nd.id);
        await updateDeviceSettings(deviceId, { ...device.settings, defaultDataset: nd.id });
      }
      setNewDatasetName("");
      await fetchDatasets();
    } catch (err) {
      console.error("Failed to create dataset:", err);
    } finally {
      setCreatingDataset(false);
    }
  };

  const CopyIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
  const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  // Latest single-value metrics to show as status badges (fault + tank)
  const latestFault = metricsData['fault']?.slice(-1)[0] ?? null;
  const latestTank = metricsData['tank']?.slice(-1)[0] ?? null;
  const restUrl = device?.settings?.restUrl || '—';

  return (
    <div style={{ marginTop: '1rem' }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1rem', padding: '1rem',
        background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
        borderRadius: '12px', border: `1px solid ${THEME_COLOR}30`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ fontSize: '1.5rem' }}>🌐</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#fff' }}>{device.name}</div>
            <span style={{
              display: 'inline-block', padding: '2px 8px', borderRadius: '12px',
              fontSize: '0.75rem', fontWeight: 600,
              backgroundColor: 'rgba(0, 188, 212, 0.2)', border: `1px solid ${THEME_COLOR}`,
              color: '#4dd0e1',
            }}>
              {device.deviceTypes?.name || 'REST'}
            </span>
            {latestFault !== null && (
              <span style={{
                display: 'inline-block', padding: '2px 8px', borderRadius: '12px',
                fontSize: '0.75rem', fontWeight: 600,
                backgroundColor: latestFault === 0 ? 'rgba(0,255,136,0.15)' : 'rgba(244,67,54,0.2)',
                border: `1px solid ${latestFault === 0 ? '#00ff88' : '#f44336'}`,
                color: latestFault === 0 ? '#00ff88' : '#f44336',
              }}>
                {latestFault === 0 ? 'No Fault' : `Fault: ${latestFault}`}
              </span>
            )}
            {latestTank !== null && (
              <span style={{
                display: 'inline-block', padding: '2px 8px', borderRadius: '12px',
                fontSize: '0.75rem', fontWeight: 600,
                backgroundColor: 'rgba(108,117,125,0.15)', border: '1px solid #6c757d',
                color: '#9e9e9e',
              }}>
                Tank {latestTank}
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {loading && <span style={{ color: THEME_COLOR, fontSize: '0.85rem' }}>Loading...</span>}
          <button
            onClick={() => setSettingsModalOpen(true)}
            style={{
              padding: '0.5rem 1rem', borderRadius: '6px',
              border: `1px solid ${THEME_COLOR}40`, background: `${THEME_COLOR}25`,
              color: THEME_COLOR, fontSize: '0.85rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}
          >
            <span>⚙️</span> Settings
          </button>
          <span style={{ color: '#6c757d', fontSize: '0.85rem', marginLeft: '0.5rem' }}>Time range:</span>
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value as any)}
            style={{
              padding: '0.5rem 1rem', borderRadius: '6px',
              border: `1px solid ${THEME_COLOR}40`,
              background: 'rgba(33, 40, 44, 0.95)', color: '#c5c5c5',
              fontSize: '0.85rem', cursor: 'pointer',
            }}
          >
            <option value="live">Live</option>
            <option value="1h">Last 1 hour</option>
            <option value="6h">Last 6 hours</option>
            <option value="24h">Last 24 hours</option>
          </select>
        </div>
      </div>

      {/* Info Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
        {/* Status */}
        <div style={{
          background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
          border: `1px solid ${isOnline ? '#00ff8830' : '#ff336630'}`,
          borderRadius: '10px', padding: '1rem',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          <div style={{ fontSize: '0.65rem', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
            Status
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', flex: 1 }}>
            <div style={{
              width: '8px', height: '8px', borderRadius: '50%',
              backgroundColor: isOnline ? '#00ff88' : '#ff3366',
              boxShadow: isOnline ? '0 0 6px #00ff88' : '0 0 6px #ff3366',
            }} />
            <span style={{ fontSize: '0.95rem', fontWeight: 600, color: isOnline ? '#00ff88' : '#ff3366' }}>
              {isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>

        {/* REST URL */}
        <div style={{
          background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
          border: `1px solid ${THEME_COLOR}20`, borderRadius: '10px', padding: '1rem',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          <div style={{ fontSize: '0.65rem', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
            Endpoint URL
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '0.85rem', fontWeight: 600, color: '#fff', fontFamily: 'monospace',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0,
            }}>
              {restUrl}
            </div>
            {restUrl !== '—' && (
              <button
                onClick={() => copyToClipboard(restUrl, 'url')}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px', flexShrink: 0, color: copiedField === 'url' ? '#00ff88' : '#6c757d', transition: 'color 0.2s' }}
              >
                {copiedField === 'url' ? <CheckIcon /> : <CopyIcon />}
              </button>
            )}
          </div>
        </div>

        {/* Token */}
        <div style={{
          background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
          border: `1px solid ${THEME_COLOR}20`, borderRadius: '10px', padding: '1rem',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          <div style={{ fontSize: '0.65rem', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
            Device Token
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '0.85rem', fontWeight: 600, color: '#fff', fontFamily: 'monospace',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0,
            }}>
              {device?.token || '—'}
            </div>
            {device?.token && (
              <button
                onClick={() => copyToClipboard(device?.token, 'token')}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px', flexShrink: 0, color: copiedField === 'token' ? '#00ff88' : '#6c757d', transition: 'color 0.2s' }}
              >
                {copiedField === 'token' ? <CheckIcon /> : <CopyIcon />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Metric Cards + Charts */}
      {chartGroups.length > 0 ? (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '0.75rem', marginBottom: '1.5rem',
          }}>
            {chartGroups.flatMap((group) =>
              group.data.datasets.map((ds: any, idx: number) => {
                const latest = ds.data[ds.data.length - 1];
                const config = Object.values(REST_METRICS_CONFIG).find((c) => c.color === ds.borderColor);
                const decimals = config?.decimals ?? 2;
                return (
                  <div key={`${group.key}-${idx}`} style={{
                    background: `linear-gradient(135deg, ${ds.borderColor}15 0%, ${ds.borderColor}08 100%)`,
                    border: `1px solid ${ds.borderColor}40`, borderRadius: '10px', padding: '1rem',
                  }}>
                    <div style={{ fontSize: '0.7rem', color: ds.borderColor, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem', fontWeight: 600 }}>
                      {ds.label}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', fontFamily: 'monospace' }}>
                      {typeof latest === 'number' ? latest.toFixed(decimals) : '—'}
                      <span style={{ fontSize: '0.75rem', opacity: 0.7, marginLeft: '4px' }}>{group.unit}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '1rem', marginBottom: '1.5rem',
          }}>
            {chartGroups.map((group) => (
              <div key={group.key} style={{
                background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
                borderRadius: '12px', padding: '1.25rem', border: `1px solid ${group.color}30`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <div style={{ width: '4px', height: '20px', background: group.color, borderRadius: '2px' }} />
                  <div style={{ fontSize: '0.9rem', color: group.color, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
                    {group.label}
                  </div>
                </div>
                <div style={{ height: '200px' }}>
                  <Line data={group.data} options={chartOptions(group.unit)} />
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{
          textAlign: 'center', color: '#6c757d', fontSize: '1rem', padding: '3rem',
          background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
          borderRadius: '12px', border: `1px solid ${THEME_COLOR}20`,
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>🌐</div>
          <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>No metrics available</div>
          <div style={{ fontSize: '0.85rem' }}>Waiting for device to send metrics data...</div>
        </div>
      )}

      {/* Settings Modal */}
      {settingsModalOpen && (
        <div className="mes-modal-overlay" onClick={() => setSettingsModalOpen(false)}>
          <div className="mes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-icon">🌐</div>
                <div className="modal-title-text">
                  <h3>REST SETTINGS</h3>
                  <p className="modal-subtitle">Configure {device.name}</p>
                </div>
              </div>
              <button className="close-btn" onClick={() => setSettingsModalOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Default Dataset</label>
                <select
                  value={selectedDatasetId}
                  onChange={handleDatasetChange}
                  style={{
                    width: '100%', padding: '0.75rem', borderRadius: '6px',
                    border: `1px solid ${THEME_COLOR}40`,
                    background: 'rgba(33, 40, 44, 0.95)', color: '#c5c5c5', fontSize: '0.9rem',
                  }}
                >
                  <option value="">Select dataset...</option>
                  {datasetOptions.map((opt: any) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label>Create New Dataset</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={newDatasetName}
                    onChange={(e) => setNewDatasetName(e.target.value)}
                    placeholder="Enter dataset name..."
                    style={{
                      flex: 1, padding: '0.75rem', borderRadius: '6px',
                      border: `1px solid ${THEME_COLOR}40`,
                      background: 'rgba(33, 40, 44, 0.95)', color: '#c5c5c5', fontSize: '0.9rem',
                    }}
                  />
                  <button
                    onClick={handleCreateDataset}
                    disabled={creatingDataset || !newDatasetName.trim()}
                    style={{
                      padding: '0.75rem 1.5rem', borderRadius: '6px',
                      border: `1px solid ${THEME_COLOR}`,
                      background: creatingDataset ? `${THEME_COLOR}50` : `${THEME_COLOR}30`,
                      color: THEME_COLOR, fontSize: '0.9rem',
                      cursor: creatingDataset || !newDatasetName.trim() ? 'not-allowed' : 'pointer',
                      opacity: creatingDataset || !newDatasetName.trim() ? 0.5 : 1,
                    }}
                  >
                    {creatingDataset ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="close-btn" onClick={() => setSettingsModalOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestDeviceMetrics;
